/**
 * 钱包服务 - 提供钱包数据获取和处理功能
 */

/**
 * 获取钱包交易摘要
 * @param {string} walletAddress - 钱包地址
 * @param {string} chain - 链名称，默认为bsc
 * @param {number} monthsBack - 获取几个月前的数据，默认为1个月
 * @returns {Promise<Object>} 钱包摘要数据
 */
export const fetchWalletSummary = async (walletAddress, chain = 'bsc', monthsBack = 120) => {
  try {
    // 获取当前日期和指定月数前的日期
    const toDate = new Date();
    const fromDate = new Date();
    fromDate.setMonth(fromDate.getMonth() - monthsBack);
    
    // 格式化日期为YYYY-MM-DD
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    const toDateStr = formatDate(toDate);
    const fromDateStr = formatDate(fromDate);
    
    // 调用API获取钱包摘要
    const apiUrl = `https://s6xvwzdvih.us-east-1.awsapprunner.com/api/web3/wallets/${walletAddress.toLowerCase()}/summary?chain=${chain}&from_date=${fromDateStr}&to_date=${toDateStr}`;
    console.log('获取钱包摘要，API地址:', apiUrl);
    
    const response = await fetch(apiUrl);
    
    if (!response.ok) {
      throw new Error(`API请求失败，状态码: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('获取到钱包摘要数据:', data);
    
    // 保存数据到localStorage，以便在页面间共享
    saveWalletData(walletAddress, data);
    
    return data;
  } catch (error) {
    console.error('获取钱包摘要失败:', error);
    return null;
  }
};

/**
 * 格式化交易摘要为可读文本
 * @param {Object} summaryData - 钱包摘要数据
 * @returns {string} 格式化后的交易摘要文本
 */
export const formatTradeSummary = (summaryData) => {
  console.log('formatTradeSummary:', summaryData);
  if (!summaryData) {
    return 'No transaction records found.';
  }

  // 如果数据结构中有data属性，则使用data属性
  const data = summaryData?.data?.data || summaryData;
  
  // 检查数据结构
  if (!data.swaps || (data.swaps && data.swaps.length === 0)) {
    if (data.holdings && data.holdings.length > 0) {
      // 如果有持仓信息但没有交易记录
      const holdingsInfo = `Holdings: ${data.holdings.map(h => {
        const symbol = h.token_symbol || h.symbol;
        const amount = h.amount || h.quantity;
        const value = h.value_usd || h.usd_amount;
        return `${symbol}: ${amount} (约 $${parseFloat(value).toFixed(2)})`;
      }).join(', ')}`;
      
      return `${holdingsInfo}\n\nNo transaction records found in the past month.`;
    }
    return 'No transaction records found.';
  }

  // 格式化持仓信息
  const holdingsInfo = data.holdings && data.holdings.length > 0 
    ? `Holdings: ${data.holdings.map(h => {
        const symbol = h.token_symbol || h.symbol;
        const amount = h.amount || h.quantity;
        const value = h.value_usd || h.usd_amount;
        return `${symbol}: ${amount} (约 $${parseFloat(value).toFixed(2)})`;
      }).join(', ')}\n\n` 
    : '';

  // 格式化盈亏信息
  let profitLossInfo = '';
  console.log('Profit/Loss information:', data.profit_loss);
  if (data.profit_loss) {
    const pl = data.profit_loss;
    profitLossInfo = `Profit/Loss:\nTotal: $${parseFloat(pl.total).toFixed(2)}\nRealized: $${parseFloat(pl.realized).toFixed(2)}\nUnrealized: $${parseFloat(pl.unrealized).toFixed(2)}\n\n`;
  }

  // 格式化最近交易
  let tradesInfo = '';
  
  // 处理新的swaps格式
  if (data.swaps && data.swaps.length > 0) {
    tradesInfo = `Recent Trades:\n${data.swaps.map(swap => {
      // 直接使用formatted字段
      return swap.formatted;
    }).join('\n')}`;
  } 
  // 兼容旧的trades格式
  else if (data.trades && data.trades.length > 0) {
    tradesInfo = `Recent Trades:\n${data.trades.map(trade => {
      // 处理不同数据结构
      const timestamp = trade.timestamp || trade.time;
      const date = new Date(timestamp * 1000).toLocaleDateString('zh-CN');
      const action = trade.action || trade.type;
      const amount = trade.amount || trade.quantity;
      const symbol = trade.token_symbol || trade.symbol;
      const price = trade.price_usd || trade.price;
      
      return `${date}: ${action} ${amount} ${symbol} ${price ? `at ${price} USD` : ''}`;
    }).join('\n')}`;
  }

  return holdingsInfo + profitLossInfo + tradesInfo;
};

/**
 * 保存钱包数据到localStorage
 * @param {string} walletAddress - 钱包地址
 * @param {Object} data - 钱包数据
 */
export const saveWalletData = (walletAddress, data) => {
  try {
    localStorage.setItem('walletData', JSON.stringify({
      address: walletAddress,
      data: data,
      timestamp: Date.now()
    }));
  } catch (error) {
    console.error('保存钱包数据到localStorage失败:', error);
  }
};

/**
 * 从localStorage获取钱包数据
 * @returns {Object|null} 钱包数据或null
 */
export const getWalletData = () => {
  try {
    const walletDataStr = localStorage.getItem('walletData');
    if (!walletDataStr) return null;
    
    const walletData = JSON.parse(walletDataStr);
    // 检查数据是否过期（超过1小时）
    const isExpired = Date.now() - walletData.timestamp > 3600000; // 1小时 = 3600000毫秒
    if (isExpired) {
      return null;
    }
    return walletData;
  } catch (error) {
    console.error('从localStorage获取钱包数据失败:', error);
    return null;
  }
};

/**
 * 检查是否需要刷新钱包数据
 * @param {string} walletAddress - 当前钱包地址
 * @returns {boolean} 是否需要刷新数据
 */
export const shouldRefreshWalletData = (walletAddress) => {
  const walletData = getWalletData();
  
  // 如果没有数据，或者地址不匹配，或者数据已过期，则需要刷新
  if (!walletData || walletData.address !== walletAddress) {
    return true;
  }
  
  // 检查数据是否过期（超过1小时）
  const isExpired = Date.now() - walletData.timestamp > 3600000; // 1小时 = 3600000毫秒
  return isExpired;
};
